import React, { useMemo, useState } from 'react'
import { Form, Input, Card, Row, Col, Collapse, Select } from 'antd'
import { CreateWebCarousel } from 'types/settings/webcontent'
import UploadSingleImage from 'components/global/UploadSingleImage'
import { Photo } from 'types/globalTypes'
import { webContentStore } from 'state/setting/webContent'
import {
  useAddSlideToWeb,
  useDeleteWebContent,
  useUpdateSlideContent,
} from 'api/Settings/WebContent'
import { Trash } from '@phosphor-icons/react'
import ButtonSpinner from 'components/global/ButtonSpinner'

const SlideForm: React.FC<{
  slideId: string | number
  index: number
  initial: CreateWebCarousel
}> = ({ slideId, index, initial }) => {
  const [form] = Form.useForm<CreateWebCarousel>()
  const {
    mutate: addSlideToWebMutation,
    isLoading: addSlideToWebMutationLoading,
  } = useAddSlideToWeb()
  const {
    mutate: updateSlideContentMutation,
    isLoading: updateSlideContentMutationLoading,
  } = useUpdateSlideContent()

  const { updateSlideContent, setGalleryId, setGalleryPhoto } =
    webContentStore()

  const [imageStates, setImageStates] = useState<{
    [key: number]: {
      id?: string
      photo?: Photo
    }
  }>({})

  const setImageId = (imgIndex: number, id: string | undefined) => {
    setImageStates(prev => ({
      ...prev,
      [imgIndex]: { ...(prev[imgIndex] || {}), id },
    }))
    // reflect in preview store so WebContent shows it immediately
    setGalleryId(imgIndex, id)
  }

  const setImagePhoto = (imgIndex: number, photo: Photo | undefined) => {
    setImageStates(prev => ({
      ...prev,
      [imgIndex]: { ...(prev[imgIndex] || {}), photo },
    }))
    // reflect in preview store so WebContent shows it immediately
    setGalleryPhoto(imgIndex, photo)
  }

  const getImageId = (imgIndex: number) => imageStates[imgIndex]?.id
  const getImagePhoto = (imgIndex: number) => imageStates[imgIndex]?.photo

  const onFinish = (values: CreateWebCarousel) => {
    const galleryData = Object.keys(imageStates)
      .map(key => {
        const imgIndex = parseInt(key)
        const imageState = imageStates[imgIndex]
        if (imageState?.id) {
          return {
            imageIndex: imgIndex,
            imageId: imageState.id,
          }
        }
        return null
      })
      .filter(Boolean) as { imageIndex: number; imageId: string }[]

    const payload = {
      content: { ...values.content },
      galleryIds: galleryData,
      type: values.type,
      videoLink: values.type === 'video' ? values.videoLink : undefined,
      imageLink: values.type === 'image' ? values.imageLink : undefined,
      commonEventId:
        values.type === 'commonEvent' ? values.commonEventId : undefined,
    }

    if ((slideId as string).includes('local-')) {
      addSlideToWebMutation(payload)
    } else {
      updateSlideContentMutation({ payload, slideId: slideId as string })
    }
  }

  const currentType = Form.useWatch('type', form)

  return (
    <Form
      form={form}
      initialValues={initial}
      layout="vertical"
      onFinish={onFinish}
      onValuesChange={(_, allValues) => {
        updateSlideContent(slideId, {
          ...initial.content,
          ...allValues.content,
          type: allValues.type,
          videoLink:
            allValues.type === 'video' ? allValues.videoLink : undefined,
          imageLink:
            allValues.type === 'image' ? allValues.imageLink : undefined,
          commonEventId:
            allValues.type === 'commonEvent'
              ? allValues.commonEventId
              : undefined,
        })
      }}
      autoComplete="off"
      className="web-editor-form"
    >
      <h3 className="text-20 w-600 mb-20px">Basic details</h3>
      <Row>
        <Col>
          <label className="input-label">Title before</label>
        </Col>
        <Col>
          <Form.Item
            className="form-control"
            name={['content', 'headingBefore']}
          >
            <Input placeholder="Title" className="form-control-field" />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col>
          <label className="input-label">Title colored</label>
        </Col>
        <Col>
          <Form.Item
            className="form-control"
            name={['content', 'headingColored']}
          >
            <Input placeholder="Title" className="form-control-field" />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col>
          <label className="input-label">Title After</label>
        </Col>
        <Col>
          <Form.Item
            className="form-control"
            name={['content', 'headingAfter']}
          >
            <Input placeholder="Title" className="form-control-field" />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col>
          <label htmlFor="" className="input-label">
            Description
          </label>
          <Form.Item name={['content', 'description']} className="form-control">
            <Input.TextArea
              className="note-field"
              placeholder="Few words about the external"
              autoSize={{ minRows: 5, maxRows: 10 }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col>
          <label className="input-label">Button text</label>
        </Col>
        <Col>
          <Form.Item className="form-control" name={['content', 'buttonText']}>
            <Input
              placeholder="ex. Learn more"
              className="form-control-field"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col>
          <label className="input-label">Button Url</label>
        </Col>
        <Col>
          <Form.Item className="form-control" name={['content', 'buttonLink']}>
            <Input
              placeholder="ex: https://www.google.com"
              className="form-control-field"
            />
          </Form.Item>
        </Col>
      </Row>

      <h3 className="text-20 w-600 mt-20px mb-20px">Gallery Images</h3>
      {Array.from({ length: 9 }, (_, imgIndex) => (
        <Row key={imgIndex} className={imgIndex > 0 ? 'mt-20px' : ''}>
          <Col>
            <label className="input-label">Image {imgIndex + 1}</label>
          </Col>
          <div>
            <UploadSingleImage
              fileId={getImageId(imgIndex)}
              setFileId={(id: string | undefined) => setImageId(imgIndex, id)}
              file={getImagePhoto(imgIndex)}
              setFile={(photo: Photo | undefined) =>
                setImagePhoto(imgIndex, photo)
              }
              sourceType="other"
            />
          </div>
        </Row>
      ))}

      <h3 className="text-20 w-600 mt-20px mb-20px">Content Type</h3>
      <Row>
        <Col span={24}>
          <Form.Item name="type" label="Type">
            <Select
              className="h-50px select-field"
              placeholder="Select content type"
              options={[
                { value: 'none', label: 'None' },
                { value: 'video', label: 'Video' },
                { value: 'image', label: 'Image' },
                { value: 'commonEvent', label: 'Event' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      {currentType === 'video' && (
        <Row>
          <Col span={24}>
            <label className="input-label">Video URL</label>
            <Form.Item className="form-control" name="videoLink">
              <Input
                placeholder="ex: https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                className="form-control-field"
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {currentType === 'image' && (
        <Row>
          <Col span={24}>
            <label className="input-label">Image URL</label>
            <Form.Item className="form-control" name="imageLink">
              <Input
                placeholder="ex: https://example.com/image.jpg"
                className="form-control-field"
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {currentType === 'commonEvent' && (
        <Row>
          <Col span={24}>
            <label className="input-label">Event ID</label>
            <Form.Item className="form-control" name="commonEventId">
              <Input
                placeholder="ex: 60d0b448b6f3a2001c8c4a0a"
                className="form-control-field"
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Form.Item>
        <div className="mt-20px flex items-center justify-end">
          <button
            className="prim-btn"
            type="submit"
            disabled={
              addSlideToWebMutationLoading || updateSlideContentMutationLoading
            }
          >
            {addSlideToWebMutationLoading || updateSlideContentMutationLoading
              ? 'Saving...'
              : 'Save'}
          </button>
        </div>
      </Form.Item>
    </Form>
  )
}

const WebEditor: React.FC = () => {
  const [clickedId, setClickedId] = useState<string | number | undefined>('')
  const { slides, addSlide, removeSlide, currentSlide, setCurrentSlide } =
    webContentStore()
  const { mutate: deleteWebContentMutation, isLoading: deleteLoading } =
    useDeleteWebContent()
  const handleDeleteSlide = (slideId: string) => {
    if (slideId.includes('local-')) {
      removeSlide(slideId)
    } else {
      setClickedId(slideId)
      deleteWebContentMutation(slideId as string, {
        onSuccess: () => {
          removeSlide(slideId)
          setClickedId(undefined)
        },
        onError: () => {
          setClickedId(undefined)
        },
      })
    }
  }
  const panels = useMemo(
    () =>
      slides.map((slide, idx) => (
        <Collapse.Panel
          header={
            <div className="flex items-center justify-between">
              <span>{`Slide ${idx + 1}`}</span>
              {clickedId === slide.id && deleteLoading ? (
                <ButtonSpinner size={20} color="#2ba7ca" />
              ) : (
                <Trash
                  size={20}
                  color="#ff6961"
                  className="pointer"
                  onClick={e => {
                    e.stopPropagation()
                    // eslint-disable-next-line no-console
                    handleDeleteSlide(slide.id as string)
                  }}
                />
              )}
            </div>
          }
          key={String(slide.id)}
        >
          <div
            className="panel-form-scroll"
            style={{
              maxHeight: 'calc(100vh - 490px)',
              overflowY: 'auto',
              paddingRight: 8,
            }}
          >
            <SlideForm slideId={slide.id} index={idx} initial={slide} />
          </div>
        </Collapse.Panel>
      )),
    [slides, removeSlide, clickedId, deleteLoading],
  )

  return (
    <>
      <div className="web-editor-container">
        <Card className="web-editor-card">
          <div className="web-editor-body">
            <Collapse
              accordion
              activeKey={
                slides[currentSlide]
                  ? String(slides[currentSlide].id)
                  : undefined
              }
              onChange={key => {
                const activeKey = Array.isArray(key) ? key[0] : key
                if (!activeKey) return
                const idx = slides.findIndex(
                  s => String(s.id) === String(activeKey),
                )
                if (idx >= 0) setCurrentSlide(idx)
              }}
            >
              {panels}
            </Collapse>
            <div
              className="mt-20px"
              style={{
                position: 'sticky',
                bottom: 0,
                background: '#fff',
                paddingTop: 10,
              }}
            >
              <button className="prim-btn" type="button" onClick={addSlide}>
                Add Slide
              </button>
            </div>
          </div>
        </Card>
      </div>
    </>
  )
}

export default WebEditor
